import {
  EnvConfig,
  isAssetReadOnly,
  Media,
  MediaType,
  TRACKED_MEDIA_VIEW_TYPES,
  useCurrentAccount,
  useRecordMediaView,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  colors,
  ImageGalleryImage,
  SettingsIcon,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { AssetCard } from './assetCard';
import { AssetFilesHeader } from './assetFiles';
import { AssetFilters } from './assetFilters';
import { FilesContext } from './filesContext';
import {
  FilePreviewHeaderActions,
  FilePreviewPanel,
} from './filesFilePreviewPanel';
import { FilterPanel, FilterPanelHeaderActions } from './filesFilterPanel';
import { GalleryPanel } from './filesGalleryPanel';
import { FileRightColumn } from './filesRightColumn';
import { VideoPanel } from './filesVideoPanel';

const EditFileHeaderButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { prepareSlideTransition } = useSlideLayout();

  return (
    <Pressable
      onPress={() => {
        prepareSlideTransition(SlideAction.NEXT);
        navigate(`${location.pathname}/edit`, {
          relative: 'path',
          state: { slideLayoutInitialTransition: SlideAction.NEXT },
        });
      }}
      style={styles.settingsButton}
      accessibilityRole="button"
      accessibilityLabel="Edit file"
    >
      <SettingsIcon size={24} color={colors.slate[700]} />
    </Pressable>
  );
};

export const AssetFilePreview = () => {
  const { assetId = '', mediaId = '', issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  // 'assetCard'  → opened from the AssetCard media fold → show AssetCard in left panel
  // 'filesList'  → opened from the file list page       → show FileRightColumn in left panel
  // default to 'filesList' for direct URL access or any unknown source
  const source: 'assetCard' | 'filesList' =
    location.state?.source === 'assetCard' ? 'assetCard' : 'filesList';
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const recordMediaView = useRecordMediaView();
  const lastRecordedViewRef = useRef<string | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [isThirdPanelOpen, setIsThirdPanelOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);

  const handleSearchClose = () => setSearchKeyword('');
  const handleSearch = async (keyword: string) => setSearchKeyword(keyword);

  const allFiles: Media[] = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );

  const selectedMedia = useMemo(
    () => allFiles.find((m) => m.id === mediaId) ?? null,
    [allFiles, mediaId]
  );

  // Record document / video / link opens as activity for reporting. Fires once
  // per (workspaceAsset, media) selection — the ref dedupes re-renders so a
  // single open isn't double-counted. recordMediaView logs its own failures and
  // never rejects, so firing it fire-and-forget is safe.
  useEffect(() => {
    const workspaceAssetId = workspaceAsset?.id;
    if (!workspaceAssetId || !selectedMedia) return;
    if (!TRACKED_MEDIA_VIEW_TYPES.includes(selectedMedia.type)) return;

    const viewKey = `${workspaceAssetId}:${selectedMedia.id}`;
    if (lastRecordedViewRef.current === viewKey) return;
    lastRecordedViewRef.current = viewKey;

    void recordMediaView({
      workspaceAssetId,
      mediaId: selectedMedia.id,
      mediaType: selectedMedia.type,
    });
  }, [workspaceAsset?.id, selectedMedia, recordMediaView]);

  const imageFiles = useMemo(
    () => allFiles.filter((m) => m.type === MediaType.Image),
    [allFiles]
  );

  const galleryImages: ImageGalleryImage[] = useMemo(
    () =>
      imageFiles.map((m) => ({
        id: m.id,
        url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
        name: m.name,
      })),
    [imageFiles]
  );

  const galleryInitialIndex = useMemo(() => {
    const idx = galleryImages.findIndex((img) => img.id === mediaId);
    return idx >= 0 ? idx : 0;
  }, [galleryImages, mediaId]);

  if (!assetId) return null;

  const isImage = selectedMedia?.type === MediaType.Image;
  const isVideo = selectedMedia?.type === MediaType.Video;

  const noop = () => undefined;
  const filesContextValue = {
    galleryImages: [],
    setGalleryImages: noop,
    galleryInitialIndex: 0,
    setGalleryInitialIndex: noop,
    thirdPanelMode: 'gallery' as const,
    setThirdPanelMode: noop,
    allFiles: [],
    selectedCategoryId,
    setSelectedCategoryId,
    isThirdPanelOpen,
    setIsThirdPanelOpen,
    isAddSheetOpen,
    setIsAddSheetOpen,
  };

  // Left panel content depends on where the user navigated from:
  //  - 'assetCard'  → show the AssetCard so the user can see the asset
  //                   details and pick a different file from the media fold
  //  - 'filesList'  → show the file list so the user can switch between files
  // Back destination respects the section the user came from — activities or
  // assets — so left-column back never crosses section boundaries.
  const assetBasePath = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;

  const leftColumn =
    source === 'assetCard'
      ? {
          title: 'assets.menu.title',
          mainComponent: (
            <AssetRouteProvider
              value={{
                basePath: assetBasePath,
                listPath: issueId ? '/activities' : '/assets',
                isWorkspaceMode: false,
                issueId,
              }}
            >
              <AssetCard assetId={assetId} />
            </AssetRouteProvider>
          ),
          headerComponent: <AssetFilters assetId={assetId} />,
          showBackButton: true,
          disableScroll: false,
          backButtonTransition: SlideAction.PREV,
          backButtonTransitionBehavior: 'concurrent' as const,
          backButtonTransitionExitColumn: 'both' as const,
          onBackButtonPress: () =>
            navigate(issueId ? `/activities/${issueId}` : '/assets', {
              state: { slideLayoutInitialTransition: SlideAction.PREV },
            }),
        }
      : {
          title: 'asset.files.title',
          disableScroll: true,
          headerComponent: (
            <FilesContext.Provider value={filesContextValue}>
              <AssetFilesHeader
                onSearchClose={handleSearchClose}
                onFilterPress={() => setIsThirdPanelOpen(true)}
                searchKeyword={searchKeyword}
                onSearch={handleSearch}
                showAddButton
                canEdit
              />
            </FilesContext.Provider>
          ),
          mainComponent: (
            <FilesContext.Provider value={filesContextValue}>
              <FileRightColumn
                assetId={assetId}
                issueId={issueId}
                searchKeyword={searchKeyword}
                selectedCategoryId={undefined}
                onAllFilesLoaded={noop}
              />
            </FilesContext.Provider>
          ),
          showBackButton: true,
          backButtonTransition: SlideAction.PREV,
          backButtonTransitionBehavior: 'concurrent' as const,
          backButtonTransitionExitColumn: 'both' as const,
          onBackButtonPress: () => {
            navigate(assetBasePath, {
              state: { slideLayoutInitialTransition: SlideAction.PREV },
            });
          },
        };

  const rightColumnHeaderComponent = selectedMedia ? (
    <View style={styles.rightHeader}>
      {!isImage && !isVideo && (
        <FilePreviewHeaderActions media={selectedMedia} />
      )}
      {!isReadOnlyMode && <EditFileHeaderButton />}
    </View>
  ) : null;

  const rightColumnMain = selectedMedia ? (
    isImage ? (
      <GalleryPanel images={galleryImages} initialIndex={galleryInitialIndex} />
    ) : isVideo ? (
      <VideoPanel media={selectedMedia} />
    ) : (
      <FilePreviewPanel media={selectedMedia} />
    )
  ) : null;

  const rightColumn = {
    title:
      selectedMedia?.name && selectedMedia.name.length > 30
        ? `${selectedMedia.name.substring(0, 27)}...`
        : selectedMedia?.name ?? 'File',
    showBackButton: true,
    // When coming from the asset card the right-column back button returns to
    // the asset card view.  From the file list it returns to the file list.
    onBackButtonPress: () =>
      navigate(source === 'assetCard' ? '../..' : '../all', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent' as const,
    disableScroll: true,
    headerComponent: rightColumnHeaderComponent,
    mainComponent: rightColumnMain,
  };

  const thirdColumnDef = isThirdPanelOpen
    ? {
        title: `Filter (${allFiles.length})`,
        mainComponent: (
          <FilesContext.Provider value={filesContextValue}>
            <FilterPanel />
          </FilesContext.Provider>
        ),
        headerComponent: (
          <FilterPanelHeaderActions
            onReset={() => setSelectedCategoryId(undefined)}
          />
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsThirdPanelOpen(false),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumnDef}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const styles = StyleSheet.create({
  rightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  settingsButton: {
    padding: 8,
    borderRadius: 4,
    cursor: 'pointer' as never,
  },
});
